// Central tunables. Live-edit via DebugPanel (Tweakpane).
// Keep types simple so Tweakpane can bind directly.

import { mx_bilerp_1 } from "three/src/nodes/materialx/lib/mx_noise.js";

export const CONFIG = {
  sim: {
    tickHz: 60,
    gravity: -22.0,
    maxSubsteps: 5,
    // Slow-motion multiplier for diagnostics. 1.0 = realtime. 0.01 = 100× slowdown.
    // Affects how much real-time elapses per fixed-step accumulator unit.
    simSpeed: 1.0,
    // Diagnostic toggles
    disableTorques: false,
    showWireframes: false,
    // Debug: render Saturn as transparent in the spaceship-in-rings map so
    // the ring/star contribution behind it can be inspected. No-op on
    // other maps.
    debugSeeThroughSaturn: false,
    // Saturn orange-tint knob (0 = untouched baked gradient, 1 = fully
    // replaced with saturated orange). Pushed into SaturnSkyMaterial
    // once per frame from ArenaView. Practical useful range is ~0–0.4.
    saturnOrangeTint: 0,
    // Show the (normally hidden) HP + KO bars above the HUD. Defaults off —
    // flip on while tuning the KO curve so you can actually see what a hit
    // took off.
    showHealthBars: false,
    // Bypass rAF VSync and drive the render loop with setTimeout(0) so it
    // runs as fast as the main thread allows. Useful for profiling render
    // cost; will peg a core.
    unrestrictFps: false
  },
  arena: {
    radius: 7.0,
    thickness: 0.5,
    killY: -8.0
  },
  // Test stairs. Each step is a cuboid spanning from the ground up to its
  // top; first step faces (baseX, baseZ), subsequent steps extend along
  // the yaw forward vector (sin yaw, 0, cos yaw). 6 sets around the arena:
  // 4 climbable (stepH ≤ maxStepUpHeight = 0.93) and 2 that exceed it so
  // walking is blocked but jumping can still land on top.
  stairs: [
    // Climbable, shortest → tallest, sweeping clockwise from E.
    { baseX:  3.5,  baseZ:  0,    yaw:  Math.PI / 2,     stepH: 0.08, stepD: 0.55, count: 5, width: 1.8 }, // E  — tiny
    { baseX:  1.75, baseZ:  3.03, yaw:  Math.PI / 6,     stepH: 0.20, stepD: 0.55, count: 5, width: 1.8 }, // NE — small
    { baseX: -1.75, baseZ:  3.03, yaw: -Math.PI / 6,     stepH: 0.40, stepD: 0.55, count: 4, width: 1.8 }, // NW — medium
    { baseX: -3.5,  baseZ:  0,    yaw: -Math.PI / 2,     stepH: 0.70, stepD: 0.60, count: 3, width: 1.8 }, // W  — large (near limit)
    // Unclimbable — walking refuses, jumping doesn't.
    { baseX: -1.75, baseZ: -3.03, yaw: -5 * Math.PI / 6, stepH: 1.05, stepD: 0.70, count: 3, width: 1.8 }, // SW — just over
    { baseX:  1.75, baseZ: -3.03, yaw:  5 * Math.PI / 6, stepH: 1.75, stepD: 0.80, count: 2, width: 1.8 }  // SE — way over
  ],
  character: {
    // Body sizing (meters)
    // Shorter pelvis capsule — halved half-height. Pelvis center stays at
    // standTargetHeight, so the spine-joint anchor on the pelvis drops 0.08 m
    // in local frame; torso (and head/arms chained above) settles 0.08 m
    // lower in world space, compacting the silhouette.
    pelvisHalfHeight: 0.08,
    pelvisRadius: 0.22,
    torsoHalfHeight: 0.165,
    torsoRadius: 0.24,
    // Neck / shoulder joint anchors on the torso are held at their original
    // world-Y positions (old torsoHalfHeight of 0.22) even though the torso
    // capsule shrank — this keeps the head and arms where they are instead
    // of dropping with the torso. Values chosen to match the pre-shrink
    // world positions.
    torsoShoulderAnchorY: 0.187,
    // Neck anchor on the torso. Raised ~15% above the shoulder-calibrated
    // value so the head sits clearly above the torso instead of nesting into
    // its top surface.
    torsoNeckAnchorY: 0.408,
    headRadius: 0.286,
    upperArmHalfHeight: 0.18,
    upperArmRadius: 0.085,
    lowerArmHalfHeight: 0.18,
    lowerArmRadius: 0.075,
    // The upper leg is GONE — the lower-leg stub hangs from a single spherical
    // joint on the pelvis. `hipToLowerLegLength` is the local-Y offset of the
    // lower-leg-side anchor above the lower-leg capsule center. Derivation:
    // at straight-down rest the anchor coincides with the pelvis hip anchor,
    // so lowerLegCenterY = pelvisCenterY − pelvisHalfHeight*0.6 − hipToLowerLegLength.
    // Solving for lowerLegCenterY = footCenterHeight (0.31):
    //   hipToLowerLegLength = pelvisAboveFoot − pelvisHalfHeight*0.6
    //                       = 0.50 − 0.048 = 0.452.
    // Also caps forward leg-swing reach — stride must stay within this radius.
    hipToLowerLegLength: 0.452,
    lowerLegHalfHeight: 0.22,
    lowerLegRadius: 0.11,
    // Mass scale
    massScale: 1.0,
    // Damping
    linearDamping: 0.45,
    angularDamping: 1.4,
    // PD orientation gains in rad/s² per rad of error (inertia-scaled inside Ragdoll).
    // Critically damped at kd ≈ 2*sqrt(kp). Tuned to comfortably overcome the
    // 22 m/s² gravity load on each limb (an unanchored upper leg needs ~380
    // rad/s² to hold up its own weight; we go higher so the body stands).
    spineStiff: 320,
    spineDamp: 36,
    // Torso PD. Separate from spineStiff so we can stiffen the upper body
    // without also stiffening the pelvis. Doubled vs spineStiff to halve the
    // torso's steady-state lean under the head pull / gait forces (was reading
    // as a pronounced lean; wanted a subtle one).
    torsoStiff: 640,
    torsoDamp: 50,
    hipStiff: 480,
    hipDamp: 44,
    // Arm PD: bumped ~4× because each arm pivots at the shoulder, so the
    // effective inertia (parallel-axis) is ~5× the bone's intrinsic inertia
    // and my PD scaling uses intrinsic. Without this the arm's angular accel
    // around the shoulder is too weak to overcome gravity for grab/reach.
    shoulderStiff: 1100,
    shoulderDamp: 105, // ~1.6× critical at kp=1100 — over-damped to suppress flail
    elbowStiff: 160,
    elbowDamp: 24,
    // Neck PD — slightly softer than a rigid head (was 220/30) so there's a
    // subtle bobble lag behind torso motion, but not so loose the head flops.
    neckStiff: 180,
    neckDamp: 28,
    // Forward pull on the head during gait (m/s² at full activity). Applied
    // as a linear impulse along the character's forward direction — the PD
    // keeps the head upright, so this reads as "head being tugged forward"
    // rather than a tilt. A little pitch bleeds through because the neck
    // joint pivots from the BOTTOM of the head, which is the intended
    // "effective neck" look.
    headForwardPull: 1.0,
    // Sprint multiplier on headForwardPull — head pushes further forward
    // during a run to sell the "leaning in to gain speed" look. 2× doubles
    // the steady-state forward drift of the head during sprint.
    headForwardPullSprintMul: 1.5,
    // Small forward pitch added to the torso PD target during sprint, scaled
    // by gait activity so it fades in as the run starts. Pairs with the head
    // pull so the whole upper body reads as leaning forward.
    torsoSprintLean: 0.15,
    // One-shot head kick on sharp direction changes. When the player's wish
    // direction (birdseye, XZ plane) rotates more than this many degrees
    // between samples, an instant Δv is applied to the head in the NEW
    // direction. Stops the head trailing behind after a turn, and is
    // stronger while sprinting to overcome the extra backdraft of a run.
    headKickAngleDeg: 15,
    headKickWalkDv: 1.4,
    headKickRunDv: 3.2,
    // Diagnostic flip: when true, the continuous pull AND the sharp-turn
    // kick both point in the OPPOSITE direction (backward relative to
    // movement). Lets you A/B the effect to confirm the "forward" setting
    // is actually leaning the way you want.
    headPullBackward: false,
    // Pelvis altitude PD ("trying to stand"). Force = clamp(Kp*err - Kd*vY, 0, Max) * mass.
    // Kp/Kd are accelerations (m/s² per m of error); Max is also m/s² before mass scaling.
    // Need ≥ gravity (22) just to hover; go significantly higher to lift the rest of the
    // body too (each limb adds load through the joints).
    // Altitude PD target. Used as the FALLBACK when no foot is planted
    // (mid-jump, both knocked off). When at least one foot is planted, the
    // target is computed live as `min(plantY_feet) + pelvisAboveFoot` — so
    // the pelvis tracks the lowest foot and rides up stairs naturally
    // instead of staying pinned to a world-space altitude.
    standTargetHeight: 0.81,
    // Pelvis center above the lowest planted foot center. With the upper leg
    // collapsed to a short internal stub, the straight-chain distance is ~0.50
    // (0.096 + 0.072 + 0.072 + 0.256). Slightly above rest so the chain stays
    // under mild tension — reads as "standing upright" rather than "slumping".
    pelvisAboveFoot: 0.50,
    standKp: 800,
    standKd: 55,
    standMax: 320,
    // Move — legacy pelvis-impulse accel; no longer used for planar locomotion
    // (feet drive it now). Kept for reference / future fine-tuning.
    moveAccel: 14.0,
    moveYawRate: 7.0,
    // Continuous stride. Each foot's pelvis-relative forward offset is
    //   offset = -cos(phase) · footSlotOffset · amplitude
    // so the foot smoothly oscillates from BACK through REST to FRONT and
    // back again. Amplitude ramps with activity (feet settle to centre when
    // idle). Phase advances by pelvis travel, so stride tempo matches real
    // movement speed: slow walks produce slow, long strides; sprints produce
    // quick short strides — no snapping between slots.
    footSlotOffset: 0.30,        // peak forward / backward offset from pelvis
    stanceWidth: 0.11,           // half-spacing of feet left/right of pelvis
    footGaitStride: 0.88,        // metres of pelvis travel per half cycle (BACK→FRONT). Full cycle = 2× this.
    footMoveThreshold: 0.25,     // m/s below which feet settle under the pelvis
    stepLiftHeight: 0.28,        // sin² lift peak during the forward-swing phase of each cycle
    footTargetK: 1600,           // strong spring toward the live continuous foot target
    footTargetD: 100,
    footLandProbeDist: 0.6,      // foot-body ground raycast distance for landing detection
    footLandDownVy: 0.5,         // must be descending slower than this to count as landed
    footFindMaxDist: 3.2,        // ray distance to find ground at foot target
    footCenterHeight: 0.31,      // lowerLeg center y when foot bottom touches ground
    // Max rise between stance foot and new step plant. If the ground at
    // the step target is taller than this, the swing is refused and the
    // character can't advance in that direction. Jumping bypasses the
    // check because during a jump both feet are in the 'air' state.
    // With the short-stub leg silhouette the character can't plausibly
    // step up tall ledges — dropped to ~½ lower-leg full length.
    maxStepUpHeight: 0.45,
    // IK-style upper-leg alignment: PD torque toward "leg pointing from
    // hip at the foot, knee-flex axis = character's right". This is what
    // keeps knees pointing forward (not splaying outward) while letting
    // the leg pitch freely to match where the foot is going.
    hipIkStiff: 320,
    hipIkDamp: 36,
    // Pelvis-over-feet spring (LEGACY path, useFootDrivenHip=true). Horizontally
    // pulls the pelvis toward the midpoint of the feet — this produces the
    // "tap-dance" feel where the pelvis is reactive to the feet dragging it
    // along. Kept for revert via the useFootDrivenHip toggle below.
    pelvisFollowK: 160,
    pelvisFollowD: 22,
    // Hip-drive (NEW path). The pelvis floats smoothly toward a target
    // velocity set by input direction; feet step in a light-touch way using
    // the live pelvis position as their forward anchor. Result: hip leads
    // the gait instead of being dragged by it.
    // Why: AAA third-person movement (Gang Beasts, Party Animals, etc.) has
    // feet planted realistically but a hip that floats independently in the
    // direction of travel. The old system coupled them too tightly.
    useFootDrivenHip: false, // flip to true to restore the old foot-driven pelvis
    hipWalkSpeed: 2.4,       // m/s target while walking (~matches legacy implied speed)
    hipSprintSpeed: 3.9,     // m/s target while sprinting
    // Convergence rate (1/s) — impulse per tick closes the velocity gap by
    // k·dt. τ = 1/k; reaching 95% of target takes ~3τ. k=44 → τ≈23ms →
    // ~70ms to lock onto target velocity on press or release.
    hipAccel: 44,
    hipDecel: 44,
    // Position-hold spring applied to the pelvis while idle (wishMag ≈ 0).
    // Anchors the pelvis at its world XZ so gravity along a tilted ship
    // surface can't slide the character. Releases the moment the player
    // inputs motion; the velocity controller takes back over. Critically
    // damped: kd ≈ 2·√kp.
    hipPlantK: 220,
    hipPlantD: 30,
    jumpImpulse: 10.0,      // direct Δv in m/s (applied whole-body — every bone gets the same impulse)
    // Sprint scales. Applied on top of the WALK baseline values (1.75 m /
    // 367 ms / 0.56 m lift). Stride a bit longer than walk, cycle notably
    // faster, lift unchanged. Absolute sprint: ~2.19 m / ~282 ms / 0.56 m
    // lift. Sprint:walk per-step speed ratio ≈ 1.63×.
    sprintStepLengthMul: 1.25,
    // sprintStepDurationMul retired — cadence is speed-driven via footGaitStride,
    // so sprinting naturally produces faster flips because the pelvis moves
    // faster. Kept as a placeholder 1.0 for any legacy reader (unused).
    sprintStepDurationMul: 1.0,
    sprintStepLiftMul: 1.0,
    // Gentle mid-air horizontal control. Applied to pelvis only while both
    // feet are 'air'. Kept small — a nudge, not a jetpack.
    airControlAccel: 14.5,   // m/s² at full stick
    // Airborne leg pose. Near-straight knee + hip IK points leg downward so
    // the character lands cleanly instead of face-planting with flailing legs.
    airKneeAngle: 0.1,
    airLegHipK: 260,
    airLegHipD: 30,
    // Arm sway synced to the gait cycle. Amplitude is in radians at the
    // shoulder; stiffness/damping are lighter than grab/swing because we
    // want these to read as floppy, not purposeful.
    // Arm-stride amplitudes (radians). The forward swing is gentler than the
    // back swing so the back phase reads as a pronounced wind-up into the
    // next stride. L arm cycles with L leg: forward during its own leg-swing,
    // back while the opposite leg swings (ipsilateral, exaggerated).
    armSwingFwd: 0.924,       // ~53° forward at peak (walk) — further 0.7× reduction
    armSwingBack: 1.232,      // ~71° back at peak (walk) — matched 0.7× reduction
    armSwingFwdSprint: 2.11,  // ~1.6× previous sprint amps for a more pronounced run arm stride
    armSwingBackSprint: 2.82,
    armSwingStiff: 500,
    armSwingDamp: 45,
    // Sprint overrides for the arm PD — looser stiffness so the arm whips
    // and trails (feels alive, not locked), damping slightly under critical
    // so there's a little overshoot at each stride.
    armSwingStiffSprint: 340,
    armSwingDampSprint: 30,
    // Lateral tilt of the idle-arm target (radians). Applied around the
    // character's local +Z (forward) axis BEFORE the swing pitch, so the arm
    // sits slightly flayed out from the torso as if there were a sideways
    // gravity pulling it away. Tuned so the arm clears the torso at rest
    // (doubled from the initial 0.20 after visible scraping). Only applied
    // in the idle pose — punches and grabs keep their head-on target.
    armFlayAngle: 0.40,
    // Lateral tilt added to the upper-leg IK target, around the character's
    // forward axis. L leg tilts its foot end outward (−X for a yaw=0 char);
    // R leg mirrors. Default is half the arm flay — "very slight" per the
    // user's request. Keep it low; legs don't have a neutral rest orientation
    // like arms (their orientation is driven by foot position), so large
    // flay values fight the IK and can wobble planted feet.
    legFlayAngle: 0.40,
    // Sprint leg flay — very minimal. At full walk flay the swinging back
    // foot overshoots across the planted front foot (because the flayed leg
    // has to cut inward to reach the far plant target), which visibly crosses
    // the legs during a run.
    legFlaySprintAngle: 0.06,
    // Extra lateral offset added to the walking foot plant target (metres,
    // outward from centreline). The leg flay rotation pulls the foot inward
    // as a side-effect — widening the plant target compensates so the foot
    // actually lands out instead of scooping toward centre. "Half the rate"
    // of leg flay per the user: legFlayAngle drives ~0.20 m of knee drift,
    // footFlayOutAmount counters at ~half that.
    footFlayOutAmount: 0.03,
    // Per-hand punch cooldown (after a swing fully completes). Lowered from
    // 200ms — user wants 2.5× more punches per arm.
    punchCooldownMs: 80,
    // Jump-kick: one-shot mid-air attack. Launch feet + pelvis forward.
    // `feet` impulse is the Δv applied to each leg bone; `body` is a smaller
    // Δv applied to the rest so the whole ragdoll travels with the strike.
    jumpKickFeetDv: 14.0,
    jumpKickBodyDv: 5.0,
    jumpKickUpDv: 2.0,
    jumpKickDurationMs: 260,
    // Jump slam: when a jump is pressed but one foot is off-ground, we can
    // still jump if that foot is close enough to ground to be "thrown down"
    // in ~slamMs milliseconds. Reach is measured from foot bottom to ground.
    jumpSlamMs: 100,
    jumpSlamTimeoutMs: 220,
    // In-flight foot targeting. While both feet are airborne (jumping) we
    // forward-project the pelvis, raycast down, and spring BOTH feet toward
    // that landing spot. Without this the feet just dangle and you straddle
    // step edges. Stops the instant either foot re-plants (natural — one
    // planted + one air is the walking cycle).
    airFootForward: 0.45,   // metres ahead of pelvis to aim for
    airFootReachUp: 1.20,   // max rise above current foot Y we'll drag to (slightly > maxStepUpHeight)
    airFootSpringK: 500,
    airFootSpringD: 50,
    // Scale applied to downward-pulling component of the air-drag spring.
    // 1.0 = symmetric (feels like 4× gravity as the pull-down drags the body
    // with it). ~0.2 gives a natural fall while still reliably planting.
    airFootPullDownScale: 0.2,
    // Stamina.
    // - Lockout gate: if stamina hits 0, all gated actions (sprint, punch,
    //   jump, jumpkick, grab) are disabled until stamina refills to 1.
    // - Costs are in "bar fraction" (1.0 = full bar).
    // - Regen waits `staminaRegenDelay` seconds after any spend, UNLESS the
    //   spend hit 0 exactly — then regen starts immediately.
    // All costs / drains divided by 3 so the stamina bar lasts ~3× longer
    // in combat. Regen is unchanged — the bar refills at the same rate, it
    // just doesn't empty as fast.
    staminaRegenPerSec: 0.6,
    staminaRegenDelay: 0.5,
    staminaPunchCost: 0.04,
    staminaJumpCost: 0.04,
    staminaJumpKickCost: 0.08,
    staminaGrabDrainPerSec: 0.05,
    staminaSprintDrainPerSec: 0.073,
    // Swings
    swingWindupMs: 130,
    swingFollowMs: 220,
    swingRecoverMs: 320,
    // PD-only swing — no extra impulse needed at this gain. Slightly
    // under-damped (kd < 2*sqrt(kp)≈85) gives a bit of follow-through bounce.
    swingStiff: 1800,
    swingDamp: 70,
    // Continuous forward force on the hand during follow-through. Kept SMALL
    // so the swing reads with extra oomph but never overrides the joint
    // solver. Each-frame impulse must be much less than what the velocity
    // cap (18 m/s) would clamp.
    punchForce: 6,
    punchUpForce: 1,
    // Peak DOWN magnitude during the pullback half — yanks the arm to the
    // side instead of letting it windmill up-and-behind on leftover forward
    // momentum.
    punchPullDownForce: 3,
    // Punch envelope timings (fractions of swingFollowMs).
    //   [0, leadOut]          — arm pushes outward (clears the torso)
    //   [leadOut, pullbackStart] — main strike (forward + inward)
    //   [pullbackStart, 1]    — pullback at `pullbackMul` × strike force
    // pullbackStart is past 0.5 so the strike has more time to connect
    // before the reversal kicks in; pullbackMul < 1 leaves a little net
    // forward drift (still much less than the no-pullback version, but
    // not a sudden yank backward).
    punchLeadOutFrac: 0.15,
    punchPullbackStart: 0.65,
    punchPullbackMul: 0.54,
    // One-shot backward impulse (N·s) applied to the upper torso at the
    // moment a punch is triggered. Counteracts the forward lean caused by
    // the arm's forward impulse during follow-through — firing on windup
    // start so the body is already settling back by the time the arm
    // actually lands. Tune this to dial the kickback strength.
    punchTorsoKickback: 0.4,
    // Lateral component pushing the punching hand toward the character's own
    // centerline (cross-body) during follow-through. Without it, the hand
    // travels straight forward from a shoulder that's already offset lateral
    // to the torso, so the arm slides past an opponent's torso instead of
    // through it. Positive = inward (toward center). Set to 0 to disable,
    // negative to flip if the direction reads wrong in practice.
    punchInwardForce: 2.5,
    // Hard safety caps applied post-step. Prevents any explosion from
    // launching a bone faster than the joint solver can correct in one frame.
    maxBoneLinVel: 18,
    maxBoneAngVel: 24,
    // Self-collision collider shrink. Enabling arm↔arm / leg↔leg contact makes
    // crossed limbs register, but the capsule caps at elbows/knees overlap at
    // rest (the two joined bones share a common anchor point). Scaling the
    // capsule halfHeight pulls those caps inward past the joint anchor so the
    // solver has nothing to resolve. Visual mesh is unchanged; only the physics
    // capsule shortens. 0.70 arms / 0.75 legs are calibrated so the shrunken
    // cap sits just inside the joint anchor position.
    armColliderLengthScale: 0.70,
    legColliderLengthScale: 0.75,
    // Leg collider radius multiplier. Legs visually stay full radius; the
    // physics capsule is thinner so legs don't hard-collide with each other
    // when they cross during a stride. Independent of the arm radius (kept
    // full) because arms and legs have different cross-over visuals.
    legColliderRadiusScale: 0.8,
    // Inner torso collider radius for own-limb self-collision. The shoulder
    // joint anchor sits exactly at torsoRadius on the torso's surface, so the
    // arm axis is on that surface and its inner half overlaps the full-size
    // torso capsule. A second, narrower collider (this radius) lives on the
    // same torso body and is ONLY visible to this character's own limbs — the
    // outer full-radius collider still handles opponent and arena contacts so
    // the character's external collision area is unchanged. Default ≈
    // torsoRadius - upperArmRadius so an arm hanging from the shoulder anchor
    // grazes but doesn't penetrate.
    torsoInnerColliderRadius: 0.155,
    // Combat
    impactImpulseScale: 0.5,
    stunMsPerImpact: 220,

    // ---- Hit registration ----
    // Qualifying bones only (hands during punch follow, feet during jump-kick,
    // head during headbutt) — others never damage. Gates below run AFTER the
    // bone-type filter.
    // Bone linear speed (m/s) required before a hit counts. ~2× the old 6.0
    // floor so incidental contact no longer damages.
    hitForceSpeedMin: 0.1,
    // Strength = clamp((speed - offset) * K, min, max). Drives FX intensity +
    // knockback. min/max scale with the new force floor.
    hitStrengthMin: 0,
    hitStrengthMax: 40.0,
    hitStrengthSpeedK: 3.0,
    hitStrengthSpeedOffset: 0.0,
    // Bone velocity must be pointing AT the victim. Projected-toward speed
    // must be at least this fraction of raw speed to avoid "swinging past".
    hitVelAlignMin: 0,
    // Accuracy weights (final accuracy = clamp(Σ w_i · component_i, 0, 1)).
    accuracyAlignWeight: 0.15,
    accuracySpeedWeight: 0.85,
    accuracyFacingWeight: 0.0,
    // Bone speed that gives 1.0 on the speed-accuracy axis.
    hitIdealSpeed: 15.0,
    // Below this accuracy the hit is ignored entirely.
    accuracyFloor: 0,
    // Calibration applied to attacker bone speeds in Combat — compensates for
    // the Rapier 0.14 → 0.19 solver change (legacy PGS → TGS Soft). TGS Soft
    // uses softer joint/contact constraints by default, so PD-driven bones
    // reach lower peak velocities for the same applied torques. Multiplying
    // the read-out speed by this factor keeps the original `hitForceSpeedMin`
    // / `hitStrengthSpeedK` / `hitIdealSpeed` thresholds calibrated to the
    // *intent* of the original tuning rather than to whatever the solver
    // happens to produce. 1.0 = passthrough (legacy 0.14 behaviour). Increase
    // until punches and jump-kicks land with the impact they used to.
    hitForceVelocityScale: 0.6,

    // ---- HP (long-term health, hidden) ----
    // 10 KOs to deplete. Every registered hit chips a tiny amount; each KO
    // charges the remainder of an HP/10 chunk.
    hpMax: 100,
    hpKOChunk: 0.10,              // HP_max × 0.10 per KO -> ~10 KOs to waste
    hpChipFractionOfKO: 0.04,     // 1/25 of an HP-per-KO chunk per hit at ≥80% accuracy
    hpAccuracyThreshold: 0.80,    // ≥threshold = full chip; below scales proportionally

    // ---- KO bar ("wakeness", hidden) ----
    // Tuned so a KO is EARNED: a perfect hit takes ~35% of the bar (so
    // ~3 perfect hits to KO), a committed punch takes ~8-15%, a grazing hit
    // takes ~4%. Sustained pressure with mid-quality hits barely beats the
    // regen, so a KO really wants a clean combo or a strong headbutt.
    //
    // Math sketch with these defaults:
    //   forceScalar = min(1.5, strength / koChipForceRef)
    //   quality     = min(1, accuracy * forceScalar * headBonus)
    //   chip        = koChipMin + (koChipMax - koChipMin) * quality^koChipExponent
    // Typical punch strength ≈ 30-45 (speed 18-23 m/s), so forceScalar ≈
    // 0.5-0.75 — no more instant-saturation at 1.5 the way it was before.
    koMax: 0.25,                   // bar fraction 0..1
    koRegenPerSec: 0.07,          // ~14s to refill from empty — sustained pressure can threaten a KO
    koRegenDelay: 2,            // seconds after hit before regen resumes
    koChipMin: 0.04,              // grazing hit — ~4% of bar
    koChipMax: 0.25,              // perfect hit — ~35% of bar (≈3 perfect hits to KO)
    koChipExponent: 2.0,          // chip grows sharply with quality
    koChipForceRef: 60.0,         // speed where forceScalar = 1.0 (avoids early saturation)
    koChipHeadBonus: 8.5,         // headbutts hurt more but don't 1-shot
    koStunSec: 4.5,               // flopped-ragdoll window after KO
    koGracePostStandSec: 0.75,    // brief post-stand immunity so lingering bodies can't instantly re-KO

    // ---- Headbutt (new; same binding as jump-kick but grounded) ----
    headbuttWindupMs: 60,         // tighter windup — "instant accel" feel
    headbuttFollowMs: 110,        // snappier follow; head is a "qualifying attacker" during this window
    headbuttRecoverMs: 220,
    headbuttHeadDv: 14.0,         // Δv applied to head on follow-entry (must clear hitForceSpeedMin to register)
    headbuttTorsoDv: 9.0,         // upper body rides with the head so it stays at speed through contact
    headbuttPelvisDv: 4.0,        // pelvis commits too — keeps the whole body moving forward
    headbuttStaminaCost: 0.06,
    headbuttCooldownMs: 500,

    // Grab
    grabReach: 0.55,
    grabRadius: 0.18,
    grab: {
      // Cone aim: each tick while the Grab button is held, a cone from the
      // character's torso forward is searched for the nearest opponent body.
      // If empty, the cone widens over time. Hands then aim along that cone
      // target; latching still uses `grabReach` above.
      coneMinHalfAngleDeg: 25,
      coneMaxHalfAngleDeg: 80,
      coneWidenDegPerSec: 220,
      coneReach: 1.8,
      // Per-stuck-hand extra pelvis thrust along the wish direction. Lets the
      // grabber drag even a heavy victim without modifying victim mass.
      dragThrustPerHand: 3.5,
      // Double-hand hold: multiplier on gravity cancellation. Value ≥ 1
      // means the victim is weightless (or slightly buoyant) while held.
      liftUpMul: 1.0,
      // PD gains on the victim's pelvis toward the target pose. kp is rad/s²
      // per metre of error; kd critically damps against overshoot.
      liftKp: 180,
      liftKd: 26,
      // World-space target offset from the grabber's torso: `liftForwardOffset`
      // metres along the grabber's facing direction, `liftUpOffset` metres up.
      // Target Y ≈ grabber-torso + 0.55 m puts the victim's pelvis well above
      // knee height — ~1.5× a leg's worth of air clearance.
      liftForwardOffset: 0.85,
      liftUpOffset: 0.55,
      // Extra upward tilt of the attacker's arms during a double-hand hold
      // (radians; subtracted from the aim pitch to raise the arms).
      liftArmPitchRad: 0.55,
      // Throw: when both hands are on the same victim and Punch is pressed,
      // release + launch with these impulses (per unit victim body mass).
      throwForwardImpulse: 13,
      throwUpImpulse: 13,
      // KO'd / stunned victims are purely floppy and absorb the full impulse
      // directly — at the standing values they fly comically far. Scale the
      // throw impulse by this factor when the victim is limp.
      throwRagdollMul: 0.5,
      // After a throw, the attacker cannot latch a new hand for this long —
      // gives the just-thrown victim a window to move away.
      throwReleaseCooldownSec: 0.25,
      // Post-grab "unlock" window: after the last attacker's hand leaves us,
      // keep the anti-stand physics suspended (no pelvis XZ anchor, no idle
      // hip-plant spring) for this many seconds so a throw actually carries.
      // Without this window the standing PDs snap the victim back in place
      // the instant they're released.
      postGrabUnlockSec: 0.75,
      // Wriggle bar: separate 0..1 bar the victim mashes down to escape.
      wriggleMax: 1.0,
      wriggleJumpCost: 0.18,
      wriggleStrikeCost: 0.12,
      wriggleMoveK: 0.9,
      wriggleMoveCapPerSec: 0.55,
      // After a wriggle-free, block any *second* hand from latching onto the
      // victim for this long. First hand is fine.
      postWriggleLockSec: 1.0
    },
    // Surface grab + pull-up climbing.
    //   - Surface grab: when no character is in the grab cone, free hands
    //     latch onto the nearest non-character rigid body (walls, ropes,
    //     asteroids) within `searchRadius` via the same spherical joint.
    //   - Pull-up: while BOTH hands are stuck on surfaces (any surface; the
    //     two bodies need not match), holding Jump runs a wind / launch state
    //     machine — pelvis is pulled up while hands "build grip force",
    //     then everything launches upward and the surface grabs release.
    //     `pullupCooldownSec` is a surface-only re-latch block; character
    //     grabs remain available during it.
    surfaceGrab: {
      searchRadius: 0.20,
      pullupWindDuration: 0.15,
      pullupWindAccel: 60,
      pullupHandAnchorAccel: 40,
      pullupLaunchDv: 12.0,
      pullupCooldownSec: 0.4,
      pullupStaminaCost: 0.12
    },
    // Swimming. Engaged when the character's mass-weighted submersion fraction
    // crosses 50% AND no hand is currently grabbing. While swimming the punch
    // / headbutt bindings are no-ops (gated in Character.step), buoyancy on
    // head/torso/pelvis keeps the upper body bobbing at the surface, and the
    // arms drive a forward-flail pose. The water surface Y is read from the
    // map's `properties.waterY` field; absent → swim mode is permanently off.
    swim: {
      // Per-bone buoyancy. Each of head/torso/pelvis has a target Y offset
      // relative to the map's waterY — the head sits clearly ABOVE the
      // surface (so the player's face is out of the water), torso right at
      // the chest line, pelvis underwater. A PD spring acts on the depth
      // BELOW the target, so a bone above its target receives no
      // buoyancy — the spring only pushes UP. Legs receive no buoyancy at
      // all so they trail downward.
      buoyancyHeadOffsetY: 0.55,    // head center sits ~0.55 m above waterY
      buoyancyTorsoOffsetY: 0.05,   // chest right at the surface
      buoyancyPelvisOffsetY: -0.30, // pelvis just below the surface
      // PD on (target − y). Stiffness is high enough that a head dipped
      // 20 cm under the surface is shoved back up within a few ticks; the
      // damping keeps the bob from oscillating like a cork.
      buoyancyKp: 110,
      buoyancyKd: 14,
      buoyancyMaxAccel: 110,
      // Horizontal pelvis impulse acceleration while moving in water. Tuned
      // a bit higher than airControlAccel so swimming feels responsive
      // without foot-plant locomotion to back it up.
      swimAccel: 14.0,
      // Extra per-axis horizontal drag applied to every bone. Makes sharp
      // direction changes feel sluggish — a turn slows the body before the
      // new heading takes hold.
      linearDrag: 1.6,
      // Alternating freestyle-style arm flail. Each arm cycles
      // independently 180° out of phase: on its "up" half-cycle the arm
      // reaches forward AND up (out of the water, like a freestyle reach);
      // on the recovery half it drops back to horizontal forward. The
      // amplitude is gated by gait activity so a stationary swimmer just
      // has both arms outstretched ahead, while a moving one strokes.
      flailFreqHz: 1.4,
      // How much the "up" half of the cycle lifts each arm above
      // horizontal-forward (radians of additional negative pitch). 0.7 rad
      // ≈ 40° of extra lift, putting the reaching arm well above the
      // surface line.
      flailUpAmp: 0.7,
      // Outward yaw offset (radians) added to each arm's aim so left/right
      // arms diverge slightly instead of stroking through the centerline.
      flailSideFlay: 0.22,
      // Entry-splash gate. Pelvis must be moving downward at least this fast
      // (m/s) when the swim threshold is crossed, otherwise the entry
      // splash is suppressed. A calm walk-in or a slow rising water level
      // catching up to a stationary character should be silent; a fall or
      // dive plays a splash. Only the entry splash is gated — strokes still
      // fire while paddling.
      entrySplashMinDownVel: 2.5,
      // Round-robin stroke splash interval (seconds). While the player is
      // swimming AND giving movement input the splash one-shot fires every
      // N seconds, cycling through the urls array via the same nextBuffer
      // counter that footsteps use.
      strokeSplashIntervalSec: 0.55,
      // Bobbing. Modulates the per-bone buoyancy targets downward so the
      // body bobs up and down instead of pinning at a constant float
      // height. `bobAmp` is the meters the equilibrium descends below its
      // base height at the bottom of the cycle (head goes from
      // waterY+0.55 down to waterY+0.55-bobAmp, dunking under when amp
      // exceeds the head offset's slack). `bobFreqHz` is the cycles/sec
      // (0.4 Hz ≈ a 2.5s bob period — calm-water cadence).
      bobAmp: 0.7,
      bobFreqHz: 0.4,
      // Stamina drain rate while actively swimming (units / sec). Stamina
      // is normalised 0..1, so 0.10 → ~10s of continuous swimming on a
      // full bar. Drain pauses for KO'd / wasted bodies (they're not
      // "swimming", just floating).
      staminaDrainPerSec: 0.20,
      // Per-bone target Y offset above the local wave surface used by the
      // KO / wasted / jump-kick-floppy "float-flat" buoyancy variant. Each
      // bone (head, torso, arms, legs, pelvis) targets surfaceY + this,
      // with NO bob — the wave itself moves the targets so limbs rise
      // and fall with the water but don't bob in addition. Sits a bit
      // ABOVE the gravity-balance equilibrium (gravity / buoyancyKp ≈ 0.2 m
      // for the stock 22 m/s² map gravity) so the body settles just above
      // the waterline, visibly floating, instead of bobbing under the
      // surface.
      koLimbFloatOffset: 0.30,
      // KO duration applied when a swimmer drowns (stamina hit 0 in
      // water). High enough that the body just stays floating — combined
      // with `wasted = true` the stand-up branch never fires anyway, so
      // this only matters as a safety net.
      drownStunSec: 99999,
      // Water current — horizontal acceleration applied per submerged
      // bone every tick. Affects ALL bodies in the water (KO'd bodies
      // drift, live swimmers fight against it). `strength` is the m/s²
      // pulled in the (dirX, dirZ) direction (auto-normalised); set to
      // 0 to disable. Default tuned so an active swimmer (`swimAccel`
      // 14) can JUST overpower the current.
      current: {
        strength: 10.0,
        dirX: -1.0,
        dirZ: 0.0
      }
    }
  },
  camera: {
    distance: 4.5,
    height: 2.0,
    fov: 60,
    followLerp: 0.12,
    lookLerp: 0.18,
    shakeMax: 0.45,
    // Camera occlusion: cast a ray from focus → camera each frame and pull
    // the camera in if a static collider is closer than the desired distance.
    // `occlusionBuffer` is the gap (m) kept between the camera and the hit
    // point so the near-plane never punches through the wall.
    occlusionEnabled: true,
    occlusionBuffer: 0.25,
    occlusionMinDist: 0.6
  },
  fx: {
    hitstopMs: 90,
    koSlowMoIn: 320,
    koSlowMoHold: 700,
    koSlowMoOut: 380,
    koSlowMoScale: 0.22
  },
  audio: {
    // User-facing volume sliders, all 0..1. Each maps to a GainNode on
    // every live AudioBus (see GameAudio.ts). Master scales everything;
    // music and sfx are independent sub-buses below master. Per-source
    // levels (e.g. a map's natural music loudness) stay on the source —
    // these knobs are the user's bus-level overlay.
    masterVolume: 1.0,
    musicVolume: 0.5,
    sfxVolume: 1.0
  },
  controls: {
    // Mouse-look multiplier on the camera yaw/pitch update in Input.ts.
    // 1.0 is the original feel; 0.5..2.0 is the slider range.
    mouseSensitivity: 1.0,
    // When true, mouse Y is inverted (pulling down looks up).
    invertY: false
  },
  // Per-hat placement tuning. Each entry describes how its model rides the
  // head: scale + local (X, Y, Z) offset relative to the head, plus a local
  // Euler (YXZ) applied after the head's orientation.
  hats: {
    top:         { scale: 0.81,  offsetX: 0,      offsetY: 0.39,  offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    chef:        { scale: 0.72,  offsetX: 0,      offsetY: 0.45,  offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    baseballCap: { scale: 0.90,  offsetX: 0,      offsetY: 0.255, offsetZ: 0.11,  yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    beanie:      { scale: 0.64,  offsetX: 0,      offsetY: 0.42,  offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    bucket:      { scale: 0.77,  offsetX: 0,      offsetY: 0.34,  offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    cowboy:      { scale: 1.07,  offsetX: 0,      offsetY: 0.275, offsetZ: -0.06, yawDeg: -90, pitchDeg: 0, rollDeg: 9 },
    firefighter: { scale: 0.90,  offsetX: 0,      offsetY: 0.30,  offsetZ: -0.07, yawDeg: -90, pitchDeg: 0, rollDeg: 7 },
    crown:       { scale: 0.63,  offsetX: 0,      offsetY: 0.32,  offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    graduation:  { scale: 0.90,  offsetX: 0.03,   offsetY: 0.30,  offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    halo:        { scale: 0.51,  offsetX: 0,      offsetY: 0.405, offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 90 },
    party:       { scale: 0.66,  offsetX: 0,      offsetY: 0.47,  offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    pirate:      { scale: 0.90,  offsetX: 0,      offsetY: 0.23,  offsetZ: 0.03,  yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    santa:       { scale: 0.77,  offsetX: 0.065,  offsetY: 0.30,  offsetZ: -0.08, yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    sombrero:    { scale: 1.21,  offsetX: -0.015, offsetY: 0.375, offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    viking:      { scale: 1.24,  offsetX: 0,      offsetY: 0.20,  offsetZ: -0.055, yawDeg: -90, pitchDeg: 0, rollDeg: 0 },
    wizard:      { scale: 0.73,  offsetX: 0,      offsetY: 0.465, offsetZ: 0,     yawDeg: -90, pitchDeg: 0, rollDeg: 0 }
  },
  render: {
    // Image-Based Lighting (scene.environment) source. Switched live from
    // the debug panel to compare looks without code edits.
    //   'roomDefault' — three.js RoomEnvironment, no rotation. The original
    //                   "free Blender-style fill" but asymmetric (its bright
    //                   panels create a fixed bias that depends on world yaw).
    //   'roomAligned' — same env, yawed so its brightest panel agrees with
    //                   the map's sun direction. Bright side becomes "sun-lit
    //                   side" rather than an arbitrary world direction.
    //   'hemisphere'  — env disabled, replaced by a HemisphereLight (sky/
    //                   ground gradient). Fully Y-symmetric so camera yaw
    //                   doesn't change fill brightness. Loses IBL specular.
    //   'hdr'         — equirectangular HDR loaded from `iblHdrPath`.
    //                   Coherent "real sky" look; needs a file on disk.
    iblMode: 'roomDefault' as 'off' | 'roomDefault' | 'roomAligned' | 'hemisphere' | 'hdr',
    // Global multiplier on env diffuse + specular contribution (drives
    // scene.environmentIntensity). Per-material envMapIntensity stacks on top.
    iblIntensity: 0.5,
    // Yaw applied to the env cubemap (room/hdr modes), in degrees. The
    // 'roomAligned' preset seeds this from the map's sunDir; the slider
    // then lets you fine-tune from there.
    iblRotationDeg: 0,
    // Hemisphere mode controls. Only active when iblMode==='hemisphere'.
    iblHemiSky: '#cfd8e0',
    iblHemiGround: '#3a2f24',
    iblHemiIntensity: 0.6,
    // Path to the equirectangular HDR (RGBE .hdr). Loaded on first switch
    // to 'hdr' mode and cached. Drop a file at this path to use it.
    iblHdrPath: '/materials/airship_env.ktx2',
    // Shadow controls. Read by Renderer at construction time, so changing them
    // at runtime needs a renderer-level apply (dispose old map + reset
    // mapSize). User-facing settings menu writes these.
    shadowsEnabled: true,
    shadowMapSize: 1024,
    // Global render-scale multiplier applied to WebGPURenderer's pixel ratio.
    // 1.0 = native (capped at 2× DPR by Renderer); 0.5 = half-res backing.
    // Distinct from `aaTaauResolutionScale` (which only affects TAAU's
    // internal scene pass) so the user-facing "Resolution" setting works
    // regardless of AA mode.
    renderScale: 1.0,
    bloom: true,
    bloomStrength: 0.55,
    bloomRadius: 0.42,
    // Bloom extracts pixels with luminance >= threshold. Raised from 0.85
    // so the pass only fires on genuinely HDR content: lava hot-spots
    // (R=1.6), ion thruster cores, sun discs. Pixels in the normal
    // display range no longer contribute — prevents bright-but-valid
    // surfaces (cloud tops, painted metal highlights) from halo-blooming
    // into pure-white patches.
    bloomThreshold: 1.0,
    vignette: 0.32,
    saturation: 1.18,
    contrast: 1.06,
    // Anti-aliasing mode.
    //   'fxaa' — cheap screen-space AA on the final sRGB image. Fast,
    //            handles transparency/particles well, but blurs sub-pixel
    //            detail and can't recover missed features.
    //   'taau' — Temporal AA with Upscaling. Jitters the camera each frame
    //            and reconstructs the output from history + per-pixel
    //            velocity. Far sharper than FXAA on thin geometry; can
    //            ghost on fast motion. Renders the scene at
    //            `aaTaauResolutionScale` of the output resolution, so
    //            values < 1 are genuine upscaling wins.
    //   'off'  — no AA.
    // TAAU requires scene velocity, so the motion-blur MRT is implicitly
    // enabled whenever TAAU is active (even with motionBlur=false).
    aaMode: 'traa' as 'fxaa' | 'taau' | 'traa' | 'off',
    // FXAA quality preset. Writes through to the three threshold
    // uniforms below. 'ultra' matches the upstream three.js addon's
    // baked-in defaults exactly. Presets:
    //   low    — contrast 0.0833 / relative 0.166 / subpixel 0.25
    //            (FXAA-Quality-12: fast, skips more pixels, hard edges)
    //   medium — contrast 0.0625 / relative 0.125 / subpixel 0.50
    //            (FXAA-Quality-15: classic "default" tier)
    //   high   — contrast 0.0312 / relative 0.063 / subpixel 0.75
    //            (FXAA-Quality-22: most pixels processed, soft edges)
    //   ultra  — contrast 0.0312 / relative 0.063 / subpixel 1.00
    //            (FXAA-Quality-29/39: three.js default, maximum smoothing)
    fxaaQuality: 'ultra' as 'low' | 'medium' | 'high' | 'ultra',
    // Raw FXAA thresholds. The quality preset writes these; you can
    // also slide them directly for bespoke tuning.
    fxaaContrastThreshold: 0.0312,
    fxaaRelativeThreshold: 0.063,
    fxaaSubpixelBlending: 1.0,
    aaTaauResolutionScale: 1.0,
    aaTaauCurrentFrameWeight: 0.025,
    aaTaauDepthThreshold: 0.0005,
    aaTaauMaxVelocityLength: 128,
    // RCAS (FSR1 contrast-adaptive) sharpen applied to the TAAU output
    // to recover detail the temporal reconstruction softens out.
    // Sharpness is backwards: 0 = strongest, 2 = none. The pass only
    // runs when aaMode='taau' and aaTaauSharpen=true.
    aaTaauSharpen: true,
    aaTaauSharpness: 0.4,
    // TRAA (Temporal Reprojection AA — same family as TAAU but
    // same-res, no upscaling). Jitters the camera and reprojects
    // history via velocity, so it needs the velocity MRT on.
    aaTraaDepthThreshold: 0.0005,
    aaTraaMaxVelocityLength: 128,
    // When on, fast-moving sub-pixel features lean on the current
    // frame more heavily — sharper under motion, but can introduce a
    // faint square pattern artifact on very bright thin edges.
    aaTraaSubpixelCorrection: true,
    // Screen-Space Ambient Occlusion (Ground-Truth AO via three's GTAO
    // node). Off by default. When enabled, the scene pass writes a
    // view-space normal MRT target so AO doesn't have to derive normals
    // from depth (which softens edges). The AO output is a single-channel
    // R buffer; it multiplies the scene color before bloom/grade.
    //
    // Tuning lives entirely in uniforms — every knob below is hot-editable
    // from the debug panel without rebuilding the pipeline. Only the master
    // toggle and the `ssaoDenoise` flag rebuild a variant.
    //
    // Pipeline order:
    //   scene → ao(depth, normal) → [denoise] → composite (scene × ao)
    //         → TAAU/TRAA → motion blur → ...
    // Putting AO before TRAA means the temporal AA accumulates the AO
    // result so its single-frame noise averages out — that's the same
    // trick the addon's `useTemporalFiltering` flag relies on.
    ssao: true,
    // World-space sampling radius. Larger = AO reaches further but
    // washes out fine detail. 0.15 keeps AO tight to contact crevices.
    ssaoRadius: 0.15,
    // Constant-thickness assumption for occluders (world units). Larger
    // values let AO pass through thinner objects more readily.
    ssaoThickness: 0.6,
    // Distance fall-off [0..1]. Lower values widen the AO band; higher
    // values restrict AO to nearby occluders.
    ssaoDistanceFallOff: 1.0,
    // Distance exponent (recommended 1–2). Steepens the AO falloff curve.
    ssaoDistanceExponent: 1.0,
    // Scale on the raw AO factor before composite. >1 deepens; <1 lightens.
    ssaoScale: 1.0,
    // Blend strength of the AO darkening into the scene color. Composite
    // is `sceneColor * mix(1, ao, intensity)` — 0 disables visually, 1 is
    // full GTAO output. 0.35 reads as subtle contact shading without
    // making the scene look gritty.
    ssaoIntensity: 0.35,
    // Sample count per pixel. Higher = cleaner but slower. 16 is a good
    // baseline; raise to 32 for high quality, drop to 8 for low.
    ssaoSamples: 16,
    // Render the AO at this fraction of the screen. 1.0 = full-res,
    // 0.5 = quarter-area (faster). Below 0.5 starts to look chunky.
    ssaoResolutionScale: 1.0,
    // Bilateral denoise pass on the AO buffer (depth + normal aware).
    // Toggling rebuilds a pipeline variant; the radius/phi knobs are
    // live uniform writes.
    ssaoDenoise: true,
    ssaoDenoiseRadius: 5.0,
    ssaoDenoiseLumaPhi: 5.0,
    ssaoDenoiseDepthPhi: 5.0,
    ssaoDenoiseNormalPhi: 5.0,
    // Velocity-based motion blur. When on, the scene pass renders with an
    // extra MRT target holding per-pixel motion vectors (from object +
    // camera movement since last frame); a 16-sample gather along that
    // vector produces the blur. Toggling off swaps to a pipeline with no
    // MRT / blur work at all so the perf difference is real.
    // `motionBlurAmount` scales the velocity (≈ shutter length) — 0 is
    // visually off even on the blur pipeline; 0.5 is subtle; 1.5+ is loud.
    motionBlur: true,
    motionBlurAmount: 0.3,
    // 3D LUT colour grade applied as the final post-processing step.
    // `lutIntensity` blends between the ungraded image (0) and the LUT
    // output (1). Toggling `lut` off forces intensity to 0 at render time.
    lut: true,
    lutIntensity: 0.27,
    // Depth of field (bokeh). The focus plane auto-tracks the focused
    // character — each frame `dofFocusDistance` is set to the real
    // camera→character distance, so they stay sharp through every
    // camera swing / spectate switch. `dofFocusOffset` nudges the focus
    // plane in front of (negative) or behind (positive) the character in
    // metres — 0 means "exactly on them". `dofFocalLength` is how far an
    // object can be from that plane before going fully out of focus;
    // `dofBokehScale` is a unitless knob for the bokeh size.
    dof: false,
    dofFocusOffset: 0.5,
    dofFocalLength: 10.0,
    dofBokehScale: 0.8,
    // Anamorphic horizontal flares — bright pixels (above the luminance
    // threshold) smear horizontally, like a wide-aspect cinema lens.
    // `anamorphicThreshold` is the luminance cutoff; `anamorphicScale`
    // controls how far the streak spreads; `anamorphicIntensity`
    // multiplies the flare's contribution into the scene. RGB gives a
    // tint (default is the classic blue / JJ-Abrams streak; push toward
    // warm for sun / lava flares). Always baked — toggling off just
    // drives intensity to 0.
    anamorphic: true,
    anamorphicIntensity: 0.08,
    anamorphicThreshold: 1.97,
    anamorphicScale: 1.5,
    anamorphicColorR: 0.10,
    anamorphicColorG: 0.17,
    anamorphicColorB: 1.00,
    // Underwater post pass — wavey on-screen split between graded scene
    // (above) and a tinted/fogged region (below) when the follow-cam dips
    // through the water surface. FxBus computes the on-screen Y of the
    // split from camera height vs `properties.waterY`:
    //
    //     screenY = clamp(0.5 + (camY - waterY) / transitionDistance, 0, 1)
    //
    // So at the surface the split sits at the screen midline; the camera
    // has to climb `transitionDistance` metres above (or descend below)
    // before the split slides off-screen. Wave knobs control the per-X
    // wobble of the line so it reads as wavey rather than a single sweep.
    underwater: {
      // Lifts the perceived surface plane this many metres above the
      // map's `waterY`. Pulls the underwater post / audio threshold up
      // a touch so it engages just before the camera enters the
      // physical water mesh, closing the gap where the mesh has
      // alpha-faded but the post pass hadn't kicked in yet.
      surfaceBias: 0.2,
      // Per-pixel mask clearance (metres above `waterY`). Each fragment
      // is shaded as "underwater" when its reconstructed world Y sits
      // below `waterY + maskClearance`. Use a small NEGATIVE value so
      // the threshold sits a touch *below* the water surface — that way
      // water-mesh fragments themselves (worldY ≈ waterY ± wave) read
      // as ABOVE the threshold and keep their normal reflective / deep
      // look, while only genuinely-submerged stuff (character legs,
      // sub hull, scene below the wave silhouette) picks up the
      // underwater shader. Set to 0 or positive if you want the water
      // surface itself to look fogged/refracted.
      maskClearance: -0.1,
      intensityRampSec: 0.18,     // (legacy) ramp seconds — unused now that the mask is per-pixel; kept for back-compat
      color: '#0c4a6c',           // tint mixed into the underwater image; also the "bed" colour for depth fog
      fogDensity: 0.12,           // exponential depth-fog coefficient (per metre of viewZ)
      refraction: 1.2,            // strength of the screen-space refraction warp (0 = none, ~1 = natural water)
      // Forward-clip on the WaterMesh: discard fragments where
      //   dot(worldPos - camPos, camForward) < clipOffset
      // — i.e. only render the mesh starting `clipOffset` metres in
      // front of the camera. So when at / below the surface, the
      // visible mesh edge sits just in front of the camera and the
      // (vertex-displaced) silhouette of that edge is the natural
      // waterline.
      clipOffset: 1,
      // Geometric Gerstner waves on the WaterMesh's vertex shader.
      // `waveAmp` is the peak vertical displacement in metres; the
      // wave field is a sum of three sines with chosen wavelengths.
      // `waveFalloffMeters` is the distance from the camera over
      // which displacement decays to ~zero — keeps the horizon flat
      // (and visually stable at distance) while letting the water
      // near the player actually move. 0 disables waves.
      waveAmp: 1.26,
      waveFalloffMeters: 80,
      // Audio side: lowpass cutoff Hz when the camera is more than half
      // submerged (i.e. cameraY < perceived surface). Composes with the
      // KO replay's own filter writes — FxBus takes the min of the two
      // so they don't fight. BYPASS_HZ when surfaced.
      cutoffHz: 700
    },
    // Boxing-ring lava shader knobs. Each matches a uniform inside
    // LavaMaterial; defaults are the tuned baseline. `normalFlip` exists
    // because height-field-derived normals can land the fake-sun
    // highlight on the wrong side of wave crests depending on sign
    // conventions — flip it here if the highlight reads backwards.
    lava: {
      flowScale: 0.18,
      deepSpeed: 0.28,
      swellSpeed: 0.9,
      rippleSpeed: 3.0,
      rippleAmp: 0.08,
      shadeStrength: 0.55,
      normalFlip: false,
      emissiveMul: 1.0,
      // Real geometric wave displacement on the lava plane. `waveAmp` scales
      // all three wave amplitudes (per-wave A = 0.30 / 0.17 / 0.08 m, so
      // peak theoretical sum = 0.55 m × waveAmp). At 2.5 peaks can reach
      // 1.4 m which would poke up into the ring base — the ring-mask
      // knobs below fade displacement to zero near the ring footprint so
      // this never happens.
      waveAmp: 2.5,
      waveSpeed: 1.0,
      // Extra gain on the wave-derived normal contribution to the fake-sun
      // shading. Without this, slow lava slopes (~3.8° peak) would shade
      // too subtly next to the ripple-layer normals. Set to 1.0 for
      // physically-honest (barely visible) wave shading.
      waveShadeGain: 6.0,
      // Ring keep-out mask for wave displacement. Distance to the ring is
      // measured as max(|x|, |z|) (axis-aligned square, matching the ring
      // geometry). `maskStart` = fully masked, `maskEnd` = fully unmasked;
      // smoothstep between. Ring halfSize is 7.0 so start 8.0 gives a 1 m
      // clear margin beyond posts, and end 11.0 gives a 3 m ramp — wide
      // enough to read as a natural "stilled pool around the ring", not a
      // hard cutoff.
      waveRingMaskStart: 8.0,
      waveRingMaskEnd: 11.0,
      // Geometry tessellation. 300 segments across 600 m = 2 m spacing,
      // giving ~4.5 samples per shortest (9 m) wave wavelength — plenty
      // for smooth motion without blowing the triangle budget.
      planeSize: 600,
      planeSegments: 300
    },
    // Solar-prominence spurts rising from the lava around the boxing ring.
    // Kept-out region is axis-aligned square |x|,|z| ≤ halfSize+buffer so
    // no plume ever clips the ring skirt / posts / canvas.
    solarSpurts: {
      maxInstances: 64,
      spawnRate: 1.25,       // average new spurts per second
      keepOutBuffer: 1.5,    // metres beyond the ring's halfSize (bundle param)
      visibleRadius: 38,     // outer bound of spawn sampling disc
      minSeparation: 2.0,    // reject new spurt within this radius of an active one
      peakHeightMin: 1.8,
      peakHeightMax: 3.2,
      lifeMin: 1.6,
      lifeMax: 2.6,
      widthMin: 0.9,
      widthMax: 1.6
    },
    // Saturn's Rings map — procedural asteroid field streaming past the ship.
    // Rocks are dynamic Rapier bodies so they bounce off the ship's heightfield
    // and collide with players. The count/field volume were pushed up to fill
    // the near-ring visually; the sky-shader ring continues the material
    // outward past the fade edges.
    spaceshipMap: {
      rockCount: 200,
      // Size bumped substantially from the previous pass — every rock is
      // at least ~1.5× its old footprint, with the biggest up to ~4×, so
      // the field reads as real asteroids rather than pebbles.
      rockMinScale: 0.27,
      rockMaxScale: 3.8,
      rockForwardSpeed: 26,          // m/s baseline +Z velocity
      rockSpeedMinMul: 0.55,         // slowest rock = base * this
      rockSpeedMaxMul: 1.6,          // fastest rock = base * this (≈3× speed spread)
      rockConeDeg: 5,                // half-angle of launch cone around +Z
      rockSpawnDepth: 390,           // metres ahead of origin where new rocks spawn
      rockSpawnHalfWidth: 195,       // lateral spawn envelope half-extent
      rockSpawnHalfHeight: 83,       // vertical spawn envelope half-extent
      rockKeepOut: 26,               // sphere radius around origin rocks avoid spawning inside
      rockDespawnZ: 240,             // when rock z passes this, recycle it to a new spawn
      // Alpha fade at the far-spawn and near-despawn ends of the field so
      // rocks never pop in/out. Distances in metres measured from the spawn
      // and despawn z-thresholds respectively.
      rockFadeInLen: 68,
      rockFadeOutLen: 42,
      // Icosahedron subdivision — 2 => 320 tris per rock, smoother silhouette
      // than detail 1 while still cheap enough at 80 instances.
      rockDetail: 1,
      // Physics / KO tuning. At 20 m/s the rock delivers a satisfying jolt;
      // sustained hits by heavier rocks can KO.
      rockDensity: 8.0,
      rockRestitution: 0.6,
      rockHitSpeedMin: 9.0,          // below this, contacts don't apply takeImpact
      rockHitStrengthK: 2.2,         // strength = clamp((speed - offset) * K)
      rockHitStrengthOffset: 6.0,
      rockHitAccuracy: 0.75,         // fixed accuracy handed to takeImpact
      rockHitCooldownSec: 0.7,       // a single rock can't re-hit the same player faster than this
      rockBonusContactRadius: 0.2    // proximity buffer beyond physical contact distance
    },
    // Ion thrusters for the spaceship map. Two identical emitters parented
    // under the ship group so the flame trails inherit the ship's bank/tilt.
    // Per-thruster pose is in SHIP-LOCAL space (metres and degrees); flames
    // exit along the thruster's local +Z axis before the rotation is applied,
    // so the rotation aims the plume. Colours are HDR (may exceed 1.0 so
    // PostPipeline bloom picks up the hottest regions).
    ionThrusters: {
      // Per-thruster pose (ship-local). Zero defaults — tune via DebugPanel.
      thrusterA: {
        posX: 2.5, posY: -2.3, posZ: 10,
        rotX: 0, rotY: 0, rotZ: 0
      },
      thrusterB: {
        posX: -2.5, posY: -2.3, posZ: 10,
        rotX: 0, rotY: 0, rotZ: 0
      },
      // Shared shape/look.
      radius: 0.8,          // spawn ring radius
      particleSize: 0.55,    // quad billboard size (metres)
      windPower: 0.7,        // radial drift strength applied to spawn velocity
      intensity: 4.0,        // spawn-rate multiplier (0 = off)
      spawnRate: 140,        // baseline new particles / sec / thruster
      jetSpeed: 30.0,         // baseline exhaust speed along local +Z (m/s)
      jetSpeedJitter: 0.35,  // ± fraction of jetSpeed
      lifeSeconds: 0.3,      // particle lifetime
      lifeJitter: 0.15,       // ± fraction of lifeSeconds
      noiseSpeed: 2.0,       // shader flicker/scroll speed
      softness: 0.8,         // disk edge softness (0 hard, 1 mushy)
      velocityDamping: 0.8,  // per-second damping (larger = flames stall sooner)
      coneSpread: 0.25,      // extra velocity added radially outward at spawn
      // Colour stops (HDR). Young-particle gradient runs core→mid→outer from
      // the disk centre outward; aged particles cool toward the tip colour.
      coreR: 1.2, coreG: 2.4, coreB: 3.6,     // blue-white core
      midR:  1.6, midG: 0.45, midB: 2.4,      // purple ring
      outerR: 2.0, outerG: 0.55, outerB: 0.15, // orange flare
      tipR:  1.1, tipG: 0.08, tipB: 0.05,     // deep red wisp
      maxParticlesPerThruster: 400
    }
  }
};

export type Config = typeof CONFIG;
